/// <reference types="vite-plugin-svgr/client" />
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import "react-app-polyfill/stable";
import { store } from "./redux-store";
import { reportInfo, reportError } from "./helpers/error-helper";
import { serializeError } from "serialize-error";
import * as configActions from "./ducks/app/userSettings";
import * as featureActions from "./ducks/features";
import "./styles.css";
import initLuxon from "./init-luxon";
import App from "./app";
import * as Sentry from "@sentry/react";
import { replayIntegration } from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useNavigationType,
} from "react-router";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { loadApiKeys } from "./ducks/app/api-keys";

initLuxon();

Sentry.init({
  sendDefaultPii: true,
  dsn: "https://e125f02c88fed52de884916309680b40@o4507542742368256.ingest.de.sentry.io/4507542780182608",
  // Tell staging apart by host, as FeatureToggle's isStaging does. Without
  // this every staging event was tagged "production" (DISPATCHER-7H).
  environment:
    window.location.host === "staging.doranordic.dk" ? "staging" : "production",
  // No `tracePropagationTargets`: the default attaches trace headers to same-origin
  // requests (our relative /api calls), linking these traces to the server's.
  // Hotjar's own errors (e.g. Firefox NS_ERROR_FAILURE on localStorage during pagehide) aren't ours
  // to fix. This only stops Sentry from reporting them; Hotjar itself keeps running.
  denyUrls: [/script\.hotjar\.com/, /static\.hotjar\.com/],
  // Redux action breadcrumbs (see redux-store.ts) add a few dozen per dialog
  // open; keep enough to still see the requests that led up to an error.
  maxBreadcrumbs: 300,
  beforeSend(event) {
    // Luxon's throwOnInvalid=true causes MUI date pickers to throw during partial date input
    // (e.g. user typing "02" with month/year still showing as placeholders). These are not real
    // errors. We identify them by the AdapterLuxon file path in the stack frames, which survives minification.
    const frames = event.exception?.values?.flatMap(
      (v) => v.stacktrace?.frames ?? []
    );
    if (
      event.exception?.values?.some((v) =>
        v.value?.startsWith("Invalid DateTime")
      ) &&
      frames?.some((f) => f.filename?.includes("AdapterLuxon"))
    ) {
      return null;
    }
    return event;
  },
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 0.3, // Server traces inherit this sampling decision for /api calls
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  enabled: process.env.NODE_ENV !== "development",
});

// import { init } from "./socket";
//
reportInfo({ msg: "UI Session started" });

store.dispatch(configActions.init());
store.dispatch(featureActions.initialize());
store.dispatch(loadApiKeys());

window.addEventListener("error", (event) => {
  // Benign browser notice that resize notifications were deferred a frame;
  // fired as an error event with no Error object, nothing actually failed.
  if (event.message?.startsWith("ResizeObserver loop")) {
    return;
  }

  const serializedError = serializeError(event.error ?? event.message);

  // Error thrown by Luxon's throwOnInvalid setting, ignore
  if (serializedError.message?.startsWith("Invalid DateTime")) {
    return;
  }

  console.log(event);
  reportError({
    msg: "uncaught error",
    err: serializedError,
  });
});

const container = document.querySelector("main");
const root = createRoot(container!);
root.render(
  <Provider store={store}>
    <App />
  </Provider>
);
